import type {
  Process,
  CreateProcessRequest,
  UpdateProcessRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function getProcesses(signal?: AbortSignal): Promise<Process[]> {
  const response = await fetch(`${API_BASE_URL}/processes`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch processes: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function createProcess(
  payload: CreateProcessRequest,
  signal?: AbortSignal
): Promise<Process> {
  const response = await fetch(`${API_BASE_URL}/processes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create process: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

export async function updateProcess(
  id: string,
  payload: UpdateProcessRequest,
  signal?: AbortSignal
): Promise<Process> {
  const response = await fetch(`${API_BASE_URL}/processes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update process: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  const data = await response.json();
  return data.item ?? data;
}

export async function deleteProcess(id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/processes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete process: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}
